<template>
    <div class="modal-content">
        <div class="form-group">
            <label class="form-label">{{ props.label }}</label>
            <DxTextBox v-if="props.dxInputType === 'dxTextBox'" v-bind="props.dxInputProps" v-model="inputValue" :focus-state-enabled="true">
                <DxValidator v-if="props.required" ref="dxValidatorRef">
                    <DxRequiredRule :message="$t('shared.inputBox.required')" />
                </DxValidator>
            </DxTextBox>
            <DxTextArea v-if="props.dxInputType === 'dxTextArea'" v-bind="props.dxInputProps" v-model="inputValue" :focus-state-enabled="true">
                <DxValidator v-if="props.required" ref="dxValidatorRef">
                    <DxRequiredRule :message="$t('shared.inputBox.required')" />
                </DxValidator>
            </DxTextArea>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref } from 'vue';
import { modalDefinedEmits, type ToolbarReference, useModalBuilder } from '@helpers/modal-building-helper';
import { messageBoxResult } from '@components/dialogs/message-box';
import { DxButton } from 'devextreme-vue/button';
import { DxTextArea } from 'devextreme-vue/text-area';
import { DxTextBox } from 'devextreme-vue/text-box';
import type { IInputBoxProps } from './models/IInputBoxProps';
import { DxValidator, DxRequiredRule } from "devextreme-vue/validator";

const props = withDefaults(defineProps<IInputBoxProps>(), {
    required: false,
    showCancel: true,
});

defineEmits([...modalDefinedEmits]);

const inputValue = ref<string | null>(props.dxInputProps?.value ?? null);
const dxValidatorRef = ref<DxValidator>();

onMounted(() => {
    const modal = useModalBuilder();
    modal.setContext(getCurrentInstance()!);

    configureButtons(modal);
});

function configureOkHandler(modal: ReturnType<typeof useModalBuilder>, okButton: ToolbarReference<DxButton>) {
    okButton.update((w) => w.onClick = () => {
        if (!props.required) {
            const v = (inputValue.value ?? '').trim();
            modal.close({ result: messageBoxResult.ok, value: v || undefined });
            return;
        }

        const trimmed = (inputValue.value ?? '').trim();
        inputValue.value = trimmed;
        const validationResult = dxValidatorRef.value!.instance!.validate();
        if (validationResult.isValid) {
            modal.close({ result: messageBoxResult.ok, value: trimmed });
        } else {
            void dxValidatorRef.value!.instance!.focus();
        }
    });
}

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
    if (props.showCancel) {
        const { okButton, cancelButton } = modal.standardButtons.addOkCancel();
        configureOkHandler(modal, okButton);
        cancelButton.update((w) => w.onClick = () => modal.close({ result: messageBoxResult.cancel }));
    } else {
        const okButton = modal.standardButtons.addOk();
        configureOkHandler(modal, okButton);
    }
}

</script>
<style scoped lang="scss"></style>